import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip,
  Button, Alert, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, LinearProgress, Tooltip,
} from '@mui/material';
import { FileDownload, Search, AccessTime, Warning, CheckCircle, TrendingDown } from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface EmpRow {
  employeeId: string;
  name: string;
  employeeNumber?: string;
  department?: string;
  position?: string;
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  halfDays: number;
  leaveDays: number;
  wfhDays: number;
  holidayDays: number;
  workedHours: number;
  overtimeHours: number;
  lateHours: number;
}

interface ReportData {
  yearMonth: string;
  from: string;
  to: string;
  workDays: number;
  employees: EmpRow[];
}

interface Department { id: string; name: string; }

const MONTH_OPTIONS = Array.from({ length: 13 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  const ym = d.toISOString().slice(0, 7);
  return { value: ym, label: d.toLocaleString('default', { month: 'long', year: 'numeric' }) };
});

const AttendanceReport: React.FC = () => {
  const [data, setData]           = useState<ReportData | null>(null);
  const [departments, setDepts]   = useState<Department[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [yearMonth, setYearMonth] = useState(MONTH_OPTIONS[0].value);
  const [deptFilter, setDeptFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [sortBy, setSortBy]       = useState<keyof EmpRow>('absentDays');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ yearMonth });
      if (deptFilter) params.append('departmentId', deptFilter);
      const [rRes, dRes] = await Promise.all([
        apiFetch(`/api/attendance/monthly-report?${params}`),
        apiFetch('/api/departments'),
      ]);
      if (!rRes.ok) throw new Error('Failed to load report');
      const [rData, dData] = await Promise.all([rRes.json(), dRes.json()]);
      setData(rData);
      setDepts(dData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [yearMonth, deptFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const rows = data?.employees ?? [];
    let result = search
      ? rows.filter((r) =>
          r.name.toLowerCase().includes(search.toLowerCase()) ||
          r.department?.toLowerCase().includes(search.toLowerCase()),
        )
      : rows;
    return [...result].sort((a, b) => {
      const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
      return sortDir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (bv < av ? -1 : bv > av ? 1 : 0);
    });
  }, [data, search, sortBy, sortDir]);

  const handleSort = (col: keyof EmpRow) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ yearMonth });
    if (deptFilter) params.append('departmentId', deptFilter);
    apiFetch(`/api/attendance/monthly-report/export.csv?${params}`)
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `attendance-${yearMonth}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  // Stats
  const stats = useMemo(() => {
    const rows = filtered;
    if (!rows.length) return null;
    return {
      totalEmployees: rows.length,
      avgAttendance: rows.reduce((s, r) => s + (r.totalDays > 0 ? r.presentDays / r.totalDays : 0), 0) / rows.length,
      totalAbsent: rows.reduce((s, r) => s + r.absentDays, 0),
      totalOT: rows.reduce((s, r) => s + r.overtimeHours, 0),
      totalLate: rows.reduce((s, r) => s + r.lateDays, 0),
      highAbsence: rows.filter((r) => r.absentDays >= 3).length,
    };
  }, [filtered]);

  const attendancePct = (row: EmpRow) =>
    data?.workDays ? Math.min(100, Math.round(((row.presentDays + row.wfhDays + row.leaveDays) / data.workDays) * 100)) : 0;

  const SortHeader = ({ col, children }: { col: keyof EmpRow; children: React.ReactNode }) => (
    <TableSortLabel active={sortBy === col} direction={sortBy === col ? sortDir : 'asc'} onClick={() => handleSort(col)}>
      {children}
    </TableSortLabel>
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Monthly Attendance Report</Typography>
          <Typography variant="body2" color="text.secondary">
            {data ? `${data.from} to ${data.to} · ${data.workDays} working days` : 'Organisation-wide attendance summary'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FileDownload />} onClick={exportCsv} disabled={!data}>
          Export CSV
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ borderLeft: '4px solid #388e3c' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {(stats.avgAttendance * 100).toFixed(0)}%
                </Typography>
                <Typography variant="caption">Avg Attendance Rate</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ borderLeft: '4px solid #d32f2f' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" fontWeight={700} color="error">{stats.highAbsence}</Typography>
                <Typography variant="caption">⚠️ High Absence (≥3 days)</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ borderLeft: '4px solid #f57c00' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">{stats.totalLate}</Typography>
                <Typography variant="caption">Total Late Instances</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ borderLeft: '4px solid #1976d2' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" fontWeight={700} color="primary">{stats.totalOT.toFixed(0)}h</Typography>
                <Typography variant="caption">Total Overtime Hours</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Month</InputLabel>
          <Select value={yearMonth} onChange={(e) => setYearMonth(e.target.value)} label="Month">
            {MONTH_OPTIONS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Department</InputLabel>
          <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} label="Department">
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="Search…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 180 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filtered.length} employees
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160, position: 'sticky', left: 0, bgcolor: 'grey.50', zIndex: 3 }}>
                    <SortHeader col="name">Employee</SortHeader>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Dept</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>Attendance</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <SortHeader col="presentDays">Present</SortHeader>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <SortHeader col="absentDays">Absent</SortHeader>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <SortHeader col="lateDays">Late</SortHeader>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>WFH</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>Leave</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <SortHeader col="workedHours">Hours</SortHeader>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>
                    <SortHeader col="overtimeHours">OT</SortHeader>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => {
                  const pct = attendancePct(row);
                  const flagAbsence = row.absentDays >= 3;
                  return (
                    <TableRow key={row.employeeId} hover sx={{ bgcolor: flagAbsence ? '#fff8f8' : undefined }}>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: flagAbsence ? '#fff8f8' : 'background.paper', zIndex: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {flagAbsence && <Warning fontSize="small" color="error" />}
                          <Box>
                            <Typography variant="body2" fontWeight={600} noWrap>{row.name}</Typography>
                            {row.employeeNumber && <Typography variant="caption" color="text.secondary">#{row.employeeNumber}</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{row.department || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'error'}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 30 }}>{pct}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip label={row.presentDays} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {row.absentDays > 0 ? (
                          <Chip label={row.absentDays} size="small" color={flagAbsence ? 'error' : 'warning'} />
                        ) : <Typography variant="body2" color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {row.lateDays > 0
                          ? <Chip label={row.lateDays} size="small" color="warning" variant="outlined" />
                          : <Typography variant="body2" color="text.disabled">0</Typography>}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2">{row.wfhDays || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2">{row.leaveDays || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={500}>{row.workedHours}h</Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {row.overtimeHours > 0
                          ? <Chip label={`${row.overtimeHours}h`} size="small" color="primary" variant="outlined" />
                          : <Typography variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="text.secondary" py={3}>No attendance data for this period.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AttendanceReport;
